import { motion } from "framer-motion";
import { useMemo } from "react";

/**
 * SparkleBurst — a one-shot particle burst emanating from center.
 * Meant to be re-mounted (via `key`) whenever a new number is revealed.
 *
 * Renders `count` sparkles with random angle/distance/size/delay,
 * each animating outward and fading over ~1.2s.
 */
export default function SparkleBurst({ count = 26, radius = 260 }) {
  const particles = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => {
        const angle = (i / count) * 360 + (Math.random() * 20 - 10);
        const dist = radius * (0.55 + Math.random() * 0.55);
        const rad = (angle * Math.PI) / 180;
        const x = Math.cos(rad) * dist;
        const y = Math.sin(rad) * dist;
        const size = 2 + Math.random() * 6;
        const delay = Math.random() * 0.15;
        const dur = 0.9 + Math.random() * 0.6;
        return { i, x, y, size, delay, dur };
      }),
    [count, radius],
  );

  return (
    <div
      aria-hidden
      className="absolute inset-0 pointer-events-none flex items-center justify-center"
    >
      {particles.map((p) => (
        <motion.span
          key={p.i}
          initial={{ x: 0, y: 0, opacity: 1, scale: 0.6 }}
          animate={{ x: p.x, y: p.y, opacity: 0, scale: 1.1 }}
          transition={{
            duration: p.dur,
            delay: p.delay,
            ease: "easeOut",
          }}
          className="absolute rounded-full"
          style={{
            width: p.size,
            height: p.size,
            background: "#fff",
            boxShadow: "0 0 10px var(--ic-primary-glow), 0 0 22px var(--ic-primary-glow)",
          }}
        />
      ))}
    </div>
  );
}
